import { useEventListener } from '@vueuse/core'
import { onBeforeUnmount, onMounted, toValue, type MaybeRefOrGetter, type Ref } from 'vue'

export type UseHeroParallaxOptions = {
  /**
   * How fast the hero lags once the effect is engaged, as a fraction of the
   * distance the hero's bottom has travelled above the bottom of the viewport.
   * Higher = the hero lingers longer while the content slides over it.
   */
  speed?: number
  /**
   * Maximum translate (in px) applied to the hero. Caps how far the hero lags
   * (and thus how strong the slide-over looks).
   */
  maxOffset?: number
  /**
   * Extra distance (px) the hero's bottom must rise above the viewport bottom
   * before the parallax engages. Gives a moment to see the bottom of a tall
   * hero before the content starts sliding over it.
   */
  startMargin?: number
  /** Disable the effect entirely (e.g. on pages without a hero). */
  enabled?: MaybeRefOrGetter<boolean>
}

/**
 * Position-driven parallax for a hero element. The hero is translated downward
 * (so it appears to scroll slower than the opaque, higher z-index content that
 * slides over it), but the effect only engages once the hero's bottom edge has
 * reached the bottom of the viewport.
 *
 * That means a hero taller than the viewport first scrolls past normally — its
 * full content stays reachable — and the slide-over only starts once you've
 * scrolled to its bottom. A hero that fits in the viewport engages right away.
 *
 * Driven by the hero's live position (getBoundingClientRect) rather than the
 * absolute scroll offset, so it adapts to any hero height. Client-only and
 * rAF-throttled; bails out when the user prefers reduced motion.
 */
export function useHeroParallax(target: Ref<HTMLElement | null>, options: UseHeroParallaxOptions = {}) {
  const { speed = 0.4, maxOffset = Number.POSITIVE_INFINITY, startMargin = 0, enabled = true } = options

  let rafId = 0
  // The translate currently applied. Subtracted from the measured rect so the
  // computation stays independent of the transform it produces (no feedback).
  let currentOffset = 0

  const apply = () => {
    rafId = 0
    const el = target.value
    if (!el) { return }

    const rect = el.getBoundingClientRect()
    // Hero bottom in document coords, with our own transform removed so the
    // value is stable regardless of scroll position or the transform we apply.
    const docBottom = rect.bottom - currentOffset + window.scrollY
    // Scroll position at which the hero bottom reaches (viewport bottom - margin).
    // Clamped to 0 so heroes that already fit the viewport engage from the top
    // and start at offset 0 (no jump on load).
    const startScroll = Math.max(0, docBottom - window.innerHeight + startMargin)
    const travelled = window.scrollY - startScroll
    const next = Math.min(Math.max(travelled * speed, 0), maxOffset)

    currentOffset = next
    el.style.transform = next ? `translate3d(0, ${next}px, 0)` : ''
  }

  const onScroll = () => {
    if (rafId) { return }
    rafId = requestAnimationFrame(apply)
  }

  onMounted(() => {
    if (!toValue(enabled)) { return }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { return }

    const el = target.value
    if (el) { el.style.willChange = 'transform' }

    apply()
    useEventListener(window, 'scroll', onScroll, { passive: true })
    useEventListener(window, 'resize', onScroll, { passive: true })
  })

  onBeforeUnmount(() => {
    if (rafId) { cancelAnimationFrame(rafId) }
    const el = target.value
    if (el) {
      el.style.transform = ''
      el.style.willChange = ''
    }
  })
}
